import { useState, useEffect } from 'react';
import { searchClient, ALGOLIA_INDEX_NAME } from '@/lib/algolia';
import { Product } from '@/types/product';
import { useToast } from '@/components/ui/use-toast';

interface UseAlgoliaSearchProps {
  priceRange: [number, number];
  selectedBrand: string;
  selectedRating: string;
  selectedAvailability: string[];
  selectedDomain: string;
  currentQuery: string;
}

interface AlgoliaHit {
  objectID: string;
  p_title: string;
  p_price: number;
  p_image_url: string;
  p_rating_value: number;
  p_rating_count: number;
  p_ean: string;
  p_slug: string;
  p_brand: string;
  p_availability: string;
  p_attributes: Record<string, any>;
  domain: string;
}

export const useAlgoliaSearch = ({
  priceRange,
  selectedBrand,
  selectedRating,
  selectedAvailability,
  selectedDomain,
  currentQuery
}: UseAlgoliaSearchProps) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [brandFacets, setBrandFacets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const index = searchClient.initIndex(ALGOLIA_INDEX_NAME);

        let ratingFilters = [];
        if (selectedRating !== 'all_products') {
          if (selectedRating === 'no_ratings') {
            ratingFilters.push('p_rating_count = 0');
          } else if (selectedRating === 'all') {
            ratingFilters.push('p_rating_count > 0');
          } else {
            const ratingValue = parseInt(selectedRating, 10);
            if (!isNaN(ratingValue)) {
              ratingFilters.push(
                'p_rating_count > 0',
                `p_rating_value >= ${ratingValue}`,
                `p_rating_value < ${ratingValue + 1}`
              );
            }
          }
        }

        const searchOptions = {
          facetFilters: [
            ...(selectedBrand !== 'all' ? [`p_brand:${selectedBrand}`] : []),
            ...(selectedAvailability.length > 0 && !selectedAvailability.includes('all')
              ? selectedAvailability.map(availability => `p_availability:${availability}`)
              : []),
            ...(selectedDomain !== 'all' ? [`domain:${selectedDomain}`] : [])
          ],
          numericFilters: [
            `p_price:${priceRange[0]} TO ${priceRange[1]}`,
            ...ratingFilters
          ],
          facets: ['p_brand', 'domain'],
          maxValuesPerFacet: 100,
          hitsPerPage: 50000
        };

        console.log("Fetching products with options:", searchOptions);
        
        const { hits, facets } = await index.search<AlgoliaHit>(currentQuery || '', searchOptions);
        console.log("Received hits:", hits);
        console.log("Received facets:", facets);

        const transformedHits = hits.map(hit => ({
          objectID: hit.objectID,
          title: hit.p_title || 'Untitled Product',
          price: hit.p_price || 0,
          image_url: hit.p_image_url || '/placeholder.svg',
          average_rating: hit.p_rating_value || 0,
          rating_amount: hit.p_rating_count || 0,
          ean: hit.p_ean || 'N/A',
          slug: hit.p_slug || 'N/A',
          brand: hit.p_brand || 'Unknown Brand',
          in_stock: !hit.p_availability?.toLowerCase().includes('nicht'),
          p_availability: hit.p_availability || 'Unknown',
          p_attributes: hit.p_attributes || {},
          domain: hit.domain || 'Unknown Domain'
        }));

        console.log("Transformed hits:", transformedHits);
        setProducts(transformedHits);

        if (facets && facets.p_brand) {
          const brandFacetValues = Object.entries(facets.p_brand).map(([value, count]) => ({
            value,
            count: count as number
          })).sort((a, b) => b.count - a.count);
          
          console.log("Transformed brand facets:", brandFacetValues);
          setBrandFacets(brandFacetValues);
        }
      } catch (error) {
        console.error("Search error:", error);
        toast({
          title: "Search Error",
          description: "Failed to fetch products. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [priceRange, selectedBrand, selectedRating, selectedAvailability, selectedDomain, currentQuery, toast]);

  return { products, brandFacets, isLoading };
};